import { memo } from 'react';
import { Container, Flex, Link, Box, Icon } from '@qga/roo-ui/components';
import styled from '@emotion/styled';
import { HeaderContextType, useHeaderContext } from '../../HeaderContext';
import { themeGet } from 'styled-system';
import { DropdownMenu } from './DropdownMenu';
import { FocusWithin } from 'react-focus-within';
import type { FC } from 'react';

export const NavigationLinks: FC = () => {
  const { desktopLinks, onInteraction, queryString } = useHeaderContext();

  return (
    <NavigationLinksMemo
      desktopLinks={desktopLinks}
      onInteraction={onInteraction}
      queryString={queryString}
    />
  );
};

type MemoProps = Pick<
  HeaderContextType,
  'desktopLinks' | 'onInteraction' | 'queryString'
>;

const NavigationLinksMemo = memo(
  ({ desktopLinks, onInteraction, queryString }: MemoProps) => {
    const onClickHandler = (trackingName?: string) =>
      onInteraction({
        type: 'Site Navigation',
        value: `${trackingName} Selected`,
      });

    return (
      <ContainerBackground>
        <Container>
          <NavList>
            {desktopLinks.map(({ trackingName, usesQueryParams, ...link }) => {
              if (link.dropdown) {
                return (
                  <FocusWithin key={trackingName}>
                    {({ focusProps, isFocused }) => (
                      <NavListItem
                        {...focusProps}
                        className={isFocused ? 'open-dropdown-menu' : ''}
                      >
                        <NavLink {...link}>
                          {link.children}
                          <Icon name="expandMore" size={18} ml={2} />
                        </NavLink>
                        <Box className="nav-dropdown-menu">
                          <DropdownMenu
                            menuItems={link.dropdown}
                            onClick={onClickHandler}
                          />
                        </Box>
                      </NavListItem>
                    )}
                  </FocusWithin>
                );
              } else {
                return (
                  <NavListItem key={trackingName}>
                    <NavLink
                      onClick={() => onClickHandler(trackingName)}
                      {...link}
                      href={`${link.href}${
                        usesQueryParams && queryString ? `?${queryString}` : ''
                      }`}
                      to={`${link.to}${
                        usesQueryParams && queryString
                          ? link.hasGclid
                            ? `&${queryString}`
                            : `?${queryString}`
                          : ''
                      }`}
                    />
                  </NavListItem>
                );
              }
            })}
          </NavList>
        </Container>
      </ContainerBackground>
    );
  }
);

NavigationLinksMemo.displayName = 'NavigationLinksMemo';

const NavList = styled.ul`
  display: flex;
  margin: 0;
  padding: 0;
  list-style-type: none;
`;

const NavListItem = styled.li`
  .nav-dropdown-menu {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.25s ease-in-out;
    z-index: ${themeGet('zIndices.navigationDropdown')};
    position: absolute;
  }

  &:hover,
  &:focus,
  &.open-dropdown-menu {
    background-color: ${themeGet('colors.white')};

    > a,
    button {
      color: ${themeGet('colors.brand.primary')};
    }

    .nav-dropdown-menu {
      visibility: visible;
      opacity: 1;
    }
  }
`;

const NavLink = styled(Link)`
  white-space: nowrap;
  font-size: ${themeGet('fontSizes.header.base')};
  padding: ${themeGet('space.5')} ${themeGet('space.5')};
  width: 100%;
  color: ${themeGet('colors.white')};

  &:hover {
    background-color: ${themeGet('colors.white')};
    color: ${themeGet('colors.brand.primary')};
  }
`;

const ContainerBackground = styled(Flex)`
  background-color: ${themeGet('colors.greys.charcoal')};

  color: ${themeGet('colors.white')};
`;
